import axios from 'axios'
import { BASE_URL } from '@/environment/environment'

export type ReportFormat = 'pdf' | 'docx' | 'xlsx'
export type ReportContext = 'single' | 'collection'
export type ReportFilters = Record<string, string | number | boolean | Array<string | number>>

export interface ReportTemplate {
  id: string
  name: string
  description: string
  version: number
  formats: ReportFormat[]
  default_format: ReportFormat
  parameters?: ReportParameter[]
  wizard?: boolean
  references?: Array<{ title: string; url: string; note: string }>
}

export type ReportScalar = string | number | boolean | null
export type ReportRow = Record<string, ReportScalar>
export type ReportParameterValue = ReportScalar | ReportRow[]
export type ReportParameterValues = Record<string, ReportParameterValue>

export interface ReportParameter {
  key: string
  label: string
  type: 'string' | 'text' | 'date' | 'integer' | 'number' | 'boolean' | 'select' | 'employee' | 'table'
  group: string
  required: boolean
  hint: string
  default: ReportParameterValue
  max_length: number
  choices: Array<{ value: string; label: string }>
  not_before?: string | null
  different_from?: string | null
  columns?: ReportParameter[]
  min_rows?: number
  max_rows?: number
  minimum?: number | null
  maximum?: number | null
  must_be_true?: boolean
  default_from?: string | null
}

export interface PreparedReport {
  parameters: ReportParameter[]
  source: ReportSource
  selection: { description: string; items: Array<{ id: string; label: string }> }
}

export interface ReportSource {
  type: string
  id?: string | number
  ids?: Array<string | number>
  filters?: ReportFilters
  sort?: string
}

const base = String(BASE_URL || '').replace(/\/$/, '')

export async function getReportTemplates(objectType: string, context: ReportContext, objectId: string | number | undefined, signal: AbortSignal) {
  const { data } = await axios.get<{ items: ReportTemplate[] }>(`${base}/api/report-templates/`, {
    params: { object_type: objectType, context, object_id: objectId },
    signal,
  })

  return data.items
}

export function generateReport(template: string, source: ReportSource, format: ReportFormat, signal: AbortSignal, parameters: ReportParameterValues = {}) {
  return axios.post<Blob>(`${base}/api/reports/generate/`, { template, source, format, parameters }, {
    responseType: 'blob',
    signal,
  })
}

export async function prepareReport(template: string, source: ReportSource, format: ReportFormat, signal: AbortSignal, parameters: Record<string, unknown> = {}) {
  const { data } = await axios.post<PreparedReport>(`${base}/api/reports/prepare/`, { template, source, format, parameters }, { signal })
  return data
}

export interface ReportPreview {
  html: string
  tables: Array<{ title: string; shown: number; total: number }>
  truncated_text: boolean
}

export async function previewReport(template: string, source: ReportSource, format: ReportFormat, parameters: ReportParameterValues, signal: AbortSignal) {
  const { data } = await axios.post<ReportPreview>(`${base}/api/reports/preview/`, { template, source, format, parameters }, { signal })
  return data
}

export interface ReportDraftSummary {
  id: string
  title: string
  template_id: string
  template_name: string
  format: ReportFormat
  revision: number
  is_archived: boolean
  created_at: string
  updated_at: string
  last_generated_at: string | null
  generation_count: number
}
export interface ReportDraft extends ReportDraftSummary {
  template: ReportTemplate
  source: ReportSource
  parameters: ReportParameterValues
}
export interface ReportDraftWrite {
  template: string
  source: ReportSource
  format: ReportFormat
  parameters: ReportParameterValues
  title: string
  revision?: number
}
export async function listReportDrafts(params: { page: number; archived: boolean; object_type?: string; object_id?: string | number }, signal: AbortSignal) {
  const { data } = await axios.get<{ count: number; results: ReportDraftSummary[] }>(`${base}/api/report-drafts/`, { params, signal })
  return data
}
export async function getReportDraft(id: string, signal: AbortSignal) {
  const { data } = await axios.get<ReportDraft>(`${base}/api/report-drafts/${encodeURIComponent(id)}/`, { signal })
  return data
}
export async function saveReportDraft(payload: ReportDraftWrite, id: string | undefined, signal: AbortSignal) {
  const { data } = id
    ? await axios.put<ReportDraft>(`${base}/api/report-drafts/${encodeURIComponent(id)}/`, payload, { signal })
    : await axios.post<ReportDraft>(`${base}/api/report-drafts/`, payload, { signal })
  return data
}
export async function archiveReportDraft(draft: ReportDraftSummary, signal: AbortSignal) {
  await axios.patch(`${base}/api/report-drafts/${encodeURIComponent(draft.id)}/`, { is_archived: !draft.is_archived, revision: draft.revision }, { signal })
}
export function generateReportDraft(draft: ReportDraft, signal: AbortSignal) {
  return axios.post<Blob>(`${base}/api/report-drafts/${encodeURIComponent(draft.id)}/generate/`, { revision: draft.revision }, { signal, responseType: 'blob' })
}
